/**
 * React Query hooks for Compliance > CoAs.
 *
 * - useCoas(filters)        GET /v1/compliance/coas (offset paginated)
 * - useCoaFilters()         GET /v1/compliance/coas/filters
 * - useCoaDetail(id)        GET /v1/compliance/coas/{id}
 * - useEditCoa()            PUT /v1/compliance/coas/{id}
 * - useCoaItem(itemNumber)  GET /v1/compliance/coas/items/{itemNumber}
 * - downloadCoasXlsx()      GET /v1/compliance/coas/export
 * - coaPdfUrl(id)           GET /v1/compliance/coas/{id}/pdf (embedded viewer)
 *
 * Gated by the compliance.coas access slug (grid/detail/export/edit). Holding
 * the slug confers full access to the tool; super-admins bypass it. Mirrors
 * useOrders.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut, type ApiError } from './api';
import { API_BASE } from './apiBase';

export const COMPLIANCE_API_BASE = `${API_BASE}/v1/compliance/coas`;

export type CoaStatus = 'unmatched' | 'filed' | 'missing' | 'not_missing';

export const DOC_TYPE_LABELS: Record<string, string> = {
  coa: 'Certificate of Analysis',
  allergen_statement: 'Allergen statement',
  kosher: 'Kosher certificate',
  letter_of_guarantee: 'Letter of guarantee',
  nutritional: 'Nutritional info',
  other: 'Other / not a CoA',
};

export type CoaRow = {
  document_id: string;
  po_id: number | null;
  file_name: string;
  sender_name: string | null;
  sender_email: string | null;
  subject: string | null;
  received_at: string | null;
  doc_type: string;
  po_number: string | null;
  po_normalized: string | null;
  status: string;
  po_source: string | null;
  confidence: string | null;
  sharepoint_web_url: string | null;
  locked: number;
  item_number: string | null;
  manufacturer: string | null;
};

export type ListCoasResponse = {
  coas: CoaRow[];
  total_count: number;
  limit: number;
  offset: number;
};

export type CoaFilters = {
  q?: string;
  status?: string;
  doc_type?: string[];
  supplier?: string;
};

export type FilterOption = { value: string; label: string; sublabel?: string };
export type CoaFilterOptions = {
  statuses: FilterOption[];
  doc_types: FilterOption[];
  suppliers: FilterOption[];
};

function buildQs(f: CoaFilters, offset: number): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.status) p.set('status', f.status);
  if (f.doc_type && f.doc_type.length > 0) p.set('doc_type', f.doc_type.join(','));
  if (f.supplier) p.set('supplier', f.supplier);
  p.set('limit', '50');
  if (offset) p.set('offset', String(offset));
  return p.toString();
}

export function useCoas(filters: CoaFilters = {}) {
  const query = useInfiniteQuery<ListCoasResponse, Error>({
    queryKey: ['compliance-coas', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListCoasResponse>(`${COMPLIANCE_API_BASE}?${buildQs(filters, (pageParam as number) ?? 0)}`),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.coas.length, 0);
      return loaded < lastPage.total_count ? loaded : undefined;
    },
    staleTime: 15_000,
    retry: false,
  });
  const rows: CoaRow[] = (query.data?.pages ?? []).flatMap((p) => p.coas);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
    refetch: () => query.refetch(),
  };
}

export function useCoaFilters() {
  const query = useQuery<CoaFilterOptions, Error>({
    queryKey: ['compliance-coas-filters'],
    queryFn: () => apiGet<CoaFilterOptions>(`${COMPLIANCE_API_BASE}/filters`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { options: query.data, isLoading: query.isLoading, error: query.error };
}

export type CoaPo = {
  po_id: number;
  po_number: string;
  po_normalized: string;
  status: string;
  sharepoint_folder: string | null;
  sharepoint_web_url: string | null;
};

export type CoaDetail = {
  document_id: string;
  file_name: string;
  sender_email: string | null;
  sender_name: string | null;
  subject: string | null;
  received_at: string | null;
  doc_type: string;
  sharepoint_item_id: string | null;
  sharepoint_web_url: string | null;
  po_source: string | null;
  confidence: string | null;
  locked: number;
  last_edited_by: string | null;
  last_edited_at: string | null;
  status: string;
  item_number: string | null;
  manufacturer: string | null;
  pos: CoaPo[];
};

export function useCoaDetail(documentId: string | null | undefined) {
  return useQuery<CoaDetail, Error>({
    queryKey: ['compliance-coa', documentId],
    queryFn: () => apiGet<CoaDetail>(`${COMPLIANCE_API_BASE}/${encodeURIComponent(documentId ?? '')}`),
    enabled: !!documentId,
    retry: false,
  });
}

export type CoaItem = {
  item_number: string;
  description: string | null;
  source: 'sage' | 'counterpoint';
  inactive: boolean;
};

/**
 * Item # -> description for the CoA detail page. Searches every Sage item,
 * then the CounterPoint item master, under the CoA grant. A 404 is a real
 * answer ("not found"), so it is never retried; anything else retries once.
 */
export function useCoaItem(itemNumber: string | null | undefined) {
  return useQuery<CoaItem, ApiError>({
    queryKey: ['compliance-coa-item', itemNumber],
    queryFn: () =>
      apiGet<CoaItem>(`${COMPLIANCE_API_BASE}/items/${encodeURIComponent(itemNumber ?? '')}`),
    enabled: !!itemNumber,
    retry: (count, err) => err.status !== 404 && count < 1,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export type EditCoaBody = {
  doc_type?: string;
  supplier?: string;
  pos?: string[];
  item_number?: string;
  manufacturer?: string;
};

export function useEditCoa() {
  const qc = useQueryClient();
  return useMutation<
    { document_id: string; updated: boolean; refiled: Array<{ po: string; moved: boolean }> },
    Error,
    { documentId: string; body: EditCoaBody }
  >({
    mutationFn: ({ documentId, body }) =>
      apiPut(`${COMPLIANCE_API_BASE}/${encodeURIComponent(documentId)}`, body),
    onSuccess: (_d, { documentId }) => {
      void qc.invalidateQueries({ queryKey: ['compliance-coas'] });
      void qc.invalidateQueries({ queryKey: ['compliance-coa', documentId] });
    },
  });
}

// Direct URL for the embedded PDF viewer (browser renders it with its native
// toolbar). Cross-subdomain cookie is same-site, so credentials flow on the
// iframe GET. The detail page fetches it as a blob to handle auth/errors.
export function coaPdfUrl(documentId: string): string {
  return `${COMPLIANCE_API_BASE}/${encodeURIComponent(documentId)}/pdf`;
}

export async function downloadCoasXlsx(filters: CoaFilters): Promise<void> {
  const qs = buildQs(filters, 0);
  const res = await fetch(`${COMPLIANCE_API_BASE}/export${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? 'coas.xlsx';
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function statusLabel(s: string): string {
  switch (s) {
    case 'unmatched': return 'Unmatched';
    case 'filed': return 'Filed';
    case 'missing': return 'Missing';
    case 'not_missing': return 'Not missing';
    default: return s;
  }
}

export function docTypeLabel(s: string): string {
  return DOC_TYPE_LABELS[s] ?? s;
}

// Capitalize the first letter for display only. Stored values stay lowercase
// (e.g. confidence 'high'/'medium'/'none', po_source 'subject'/'filename').
export function capitalizeFirst(s: string | null | undefined): string {
  if (!s) return '--';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Binary access-grant model (spec 00_access-binary-grants.md): the CoAs tool is
// a single grantable slug, 'compliance.coas'. Holding it confers full access to
// the tool (there are no read/write levels), so read and write both check the
// one slug. Super-admins bypass every capability check, so each helper takes an
// explicit superAdmin flag (from user.superAdmin) like the other gating helpers.
export function hasComplianceCoaRead(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('compliance.coas');
}
export function hasComplianceCoaWrite(perms: string[] | undefined, superAdmin = false): boolean {
  return hasComplianceCoaRead(perms, superAdmin);
}
